import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { resolveFileUrl } from '@/api/client';
import {
  Button,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  Modal,
  useToast,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';

/** What a browser can draw by itself. Word files it can only save. */
function viewerFor(contentType, fileName) {
  // `?? ''`, not a default parameter: the API sends `null` for a missing
  // file, and a default only replaces `undefined`.
  const type = (contentType ?? '').toLowerCase();
  fileName = fileName ?? '';
  if (type === 'application/pdf' || /\.pdf$/i.test(fileName)) return 'pdf';
  if (type.startsWith('image/') || /\.(png|jpe?g|webp)$/i.test(fileName)) return 'image';
  return null;
}

/**
 * Fetches the file and hands back a same-origin `blob:` URL for it.
 *
 * The file store sends `X-Frame-Options: SAMEORIGIN`, so pointing the frame
 * at the signed link straight away draws Chrome's "refused" page. Its CORS
 * rules do allow our origins to read the file, and a `blob:` URL belongs to
 * this page, so the frame accepts it.
 */
async function loadAsBlobUrl(url, kind) {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Could not open the document');
  const blob = await response.blob();
  // A store that labels a PDF `octet-stream` would make the frame download it.
  const typed = kind === 'pdf' ? new Blob([blob], { type: 'application/pdf' }) : blob;
  return URL.createObjectURL(typed);
}

/**
 * A diploma or CV shown inside the page instead of downloaded.
 *
 * `loadUrl({ inline })` returns a signed link; the inline one asks the server
 * for `Content-Disposition: inline`, which is what lets a PDF render in the
 * frame. Links are short-lived, so a fresh one is fetched each time the viewer
 * opens and never cached. A Word file cannot be drawn by a browser, so it gets
 * a download button instead of an empty frame.
 */
export function DocumentViewer({ open, onClose, title, fileName, contentType, queryKey, loadUrl }) {
  const toast = useToast();
  const kind = viewerFor(contentType, fileName);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['document-view', ...queryKey],
    queryFn: async () => {
      const { download_url } = await loadUrl({ inline: true });
      return loadAsBlobUrl(resolveFileUrl(download_url), kind);
    },
    enabled: open && kind !== null,
    staleTime: 0,
    gcTime: 0,
  });
  const src = data ?? null;

  // A blob URL holds the file in memory until it is let go.
  useEffect(() => () => src && URL.revokeObjectURL(src), [src]);

  async function download() {
    try {
      const { download_url } = await loadUrl({ inline: false });
      window.open(resolveFileUrl(download_url), '_blank', 'noopener');
    } catch (downloadError) {
      toast.error(errorMessage(downloadError, 'Could not download the file'));
    }
  }

  return (
    <Modal
      open={open}
      size="xl"
      title={title}
      description={fileName}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={() => void download()}>
            <Icon name="download" size={15} />
            Download
          </Button>
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {kind === null ? (
        <EmptyState
          icon={<Icon name="fileText" size={22} />}
          title="This file can't be shown in the browser"
          description="Word documents have to be downloaded to read. PDFs and images open here."
          action={
            <Button size="sm" onClick={() => void download()}>
              <Icon name="download" size={15} />
              Download
            </Button>
          }
        />
      ) : isError ? (
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      ) : isPending || !src ? (
        <LoadingState label="Opening the document…" />
      ) : kind === 'pdf' ? (
        <iframe
          src={src}
          title={fileName || title}
          className="border-line bg-surface-muted h-[70vh] w-full rounded-lg border"
        />
      ) : (
        <div className="bg-surface-muted grid max-h-[70vh] place-items-center overflow-auto rounded-lg p-2">
          <img src={src} alt={fileName || title} className="max-w-full object-contain" />
        </div>
      )}
    </Modal>
  );
}
